import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators, FormArray } from '@angular/forms';
import { TRIP_TYPES } from 'src/app/core/enums/tripTypes.enum';
import { Tag } from 'src/app/core/interfaces/tag';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { Permission } from 'src/app/core/resources/permission';
import { MenuServiceService } from '../../menu-service/menu-service.service';
import { Utils } from 'src/app/core/resources/utils';
import { Router } from '@angular/router';
import { Company } from 'src/app/core/interfaces/company';
import { Subscription } from 'rxjs';
import { ThirdParty } from 'src/app/core/interfaces/thirdParty';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { OriginReteicaService } from 'src/app/core/services/origin-reteica.service';
import { ServiceResources } from '../../resources/services';
import { CargoResources } from 'src/app/modules/cargo/manual-creation-cargo/resources/cargo';
import { Fmt } from 'src/app/core/messages/fmt';
import { ConsignmentCargo } from 'src/app/core/interfaces/consignmentCargo';
import { NgxSpinnerService } from 'ngx-spinner';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
import { CompanyManager } from 'src/app/core/managers/company.manager';
import { CargoManager, VEHICLE_TRIP_VALIDATION_EXCEPTION_CODE } from 'src/app/core/managers/cargo.manager';
import { GuideService } from 'src/app/modules/guides/guide.service';
import { ApprovalStateEnum } from 'src/app/core/interfaces/travel-expense';
import { TransportCompany } from 'src/app/core/interfaces/transportCompany';
import {
  ChangeTransportCompanyRequest,
  ChangeTransportCompanyResponse,
  TransportCompanyChangeJob,
  TransportCompanyChangeJobStatus
} from 'src/app/core/interfaces/transport-company-change';
import { DialogComponent } from 'src/app/shared/dialog/dialog.component';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import {
  getTransportCompanyChangeStepLabel,
  shouldQueryTransportCompanyChangeJob
} from 'src/app/core/resources/transport-company-change.utils';

export enum ContractorTripInfoDialogSection {
  CONTRACTOR_TRANSPORT = 'contractor-transport',
  ADDITIONAL_INFORMATION = 'additional-information'
}

interface ContractorTripInfoDialogData {
  cargo: Cargo;
  contractorTripInfoSection?: ContractorTripInfoDialogSection;
}

//TODO: Documentar el codigo
@Component({
  selector: 'app-contractor-trip-info',
  templateUrl: './contractor-trip-info.component.html',
  styleUrls: ['./contractor-trip-info.component.scss']
})
export class ContractorTripInfoComponent implements OnInit, OnDestroy {
  permission = Permission;
  //Company
  companyControl = new FormControl('', [Validators.required]);
  companyOptions: OptionsAutocomplete = {
    title: 'Nombre',
    appearance: 'outline',
    disableCompanyIfInactive: true
  };
  companyValidate = '';
  // El required se aplica en ngOnInit: solo lo exige una SaaS de transporte
  // sobre un servicio de transporte (ver requiresTransportCompany).
  transportCompanyControl = new FormControl(null);
  private selectedCompanyId = '';
  private originalTransportCompany: TransportCompany = null;
  private transportCompanySubscription: Subscription;
  //Cargo owner address
  cargoOwnerAddressControl = new FormControl('', [Validators.required]);
  cargoOwnerAddressValidate = '';
  //Tags
  tags: Tag[] = [];
  tagsSaved: string[] = [];
  tagControl: FormControl = new FormControl('');
  //Trip types
  tripTypeControl: FormControl = new FormControl('', [Validators.required]);
  tripTypes: { label: string, name: TRIP_TYPES, isValid?: boolean, error?: string }[] = [
    { label: 'Importación', name: TRIP_TYPES.IMPORT, isValid: true, error: '' },
    { label: 'Exportación', name: TRIP_TYPES.EXPORT, isValid: true, error: '' },
    { label: 'Nacional', name: TRIP_TYPES.NATIONAL, isValid: true, error: '' },
    { label: 'Urbano', name: TRIP_TYPES.URBAN, isValid: true, error: '' },
    { label: 'Último milla/ Paqueteo urbano', name: TRIP_TYPES.LAST_MILE, isValid: true, error: '' }
  ];
  //Ministry
  ministryControl: FormControl = new FormControl(null, [Validators.required]);
  numberDocumentSenderControl: FormControl = new FormControl('');
  formSubscription: Subscription;
  isDialog: boolean = false;
  activeTransportCompanyChangeJobId: string = '';
  transportCompanyChangeJob: TransportCompanyChangeJob = null;
  transportCompanyChangeJobError: string = '';
  isLoadingTransportCompanyChangeJob: boolean = false;
  isRefreshingTransportCompanyChangeJob: boolean = false;
  isSubmittingTransportCompanyChange: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public paramsDialog: ContractorTripInfoDialogData,
    public dialogRef: MatDialogRef<ContractorTripInfoComponent>,
    public utils: Utils,
    public menuService: MenuServiceService,
    private router: Router,
    private permissionRole: PermissionRole,
    private snackBarService: SnackBarService,
    private serviceResources: ServiceResources,
    private cargoResources: CargoResources,
    private spinner: NgxSpinnerService,
    private companyManager: CompanyManager,
    private guideService: GuideService,
    private dialog: MatDialog,
    private cargoManager: CargoManager,
    private originReteicaService: OriginReteicaService
  ) { }

  async ngOnInit() {
    await this.checkDialogParams();
    if (this.menuService.isTransportService()) this.tripTypes.push({ label: 'Internacional', name: TRIP_TYPES.INTERNATIONAL });
    // Después de checkDialogParams: el cargo del diálogo es lo que define si el
    // servicio declara empresa de transporte.
    if (this.requiresTransportCompany) { this.transportCompanyControl.setValidators([Validators.required]); }
    this.setLocalValues();
    this.setSubscription();
    this.checkPermissionsToEdit();
    this.getTags();
    await this.loadActiveTransportCompanyChangeJob();
  }

  private async checkDialogParams() {
    if (this.paramsDialog && this.paramsDialog.cargo) {
      this.isDialog = true;
      this.menuService.cargo = this.paramsDialog.cargo;
      this.menuService.patchForm(this.paramsDialog.cargo, false);
      await this.menuService.setCargoConsignments();
      await this.menuService.setCargoVehicle();
    }
  }

  get dialogSection(): ContractorTripInfoDialogSection {
    return this.paramsDialog && this.paramsDialog.contractorTripInfoSection;
  }

  get isContractorTransportDialog(): boolean {
    return this.isDialog && this.dialogSection === ContractorTripInfoDialogSection.CONTRACTOR_TRANSPORT;
  }

  get isAdditionalInformationDialog(): boolean {
    return this.isDialog && this.dialogSection === ContractorTripInfoDialogSection.ADDITIONAL_INFORMATION;
  }

  get showContractorTransportSection(): boolean {
    return !this.isDialog || !this.dialogSection || this.isContractorTransportDialog;
  }

  /** Gobierna tanto el input como el required de empresa de transporte. */
  get requiresTransportCompany(): boolean {
    return this.menuService.requiresTransportCompany();
  }

  get showAdditionalInformationSection(): boolean {
    return !this.isDialog || !this.dialogSection || this.isAdditionalInformationDialog;
  }

  get dialogTitle(): string {
    return this.isContractorTransportDialog && this.requiresTransportCompany
      ? 'Contratante y empresa de transporte'
      : this.isAdditionalInformationDialog
        ? 'Información adicional del servicio'
        : 'Información del contratante';
  }

  get showTransportCompanyChangeJob(): boolean {
    return this.isContractorTransportDialog && !!this.activeTransportCompanyChangeJobId;
  }

  get transportCompanyChangeJobStatus(): TransportCompanyChangeJobStatus {
    return this.transportCompanyChangeJob ? this.transportCompanyChangeJob.status : null;
  }

  get transportCompanyChangeJobTitle(): string {
    if (this.transportCompanyChangeJobStatus === 'DONE') return 'Cambio completado';
    if (this.transportCompanyChangeJobStatus === 'FAILED') return 'No fue posible completar el cambio';
    return 'Cambio en proceso';
  }

  get transportCompanyChangeJobStatusLabel(): string {
    const labels: { [status: string]: string } = {
      PENDING: 'Pendiente',
      VALIDATING: 'Validando información',
      CANCELLING: 'Anulando documentos RNDC',
      SWITCHING: 'Aplicando cambios',
      RECREATING: 'Recreando documentos RNDC',
      DONE: 'Finalizado',
      FAILED: 'Fallido'
    };
    return labels[this.transportCompanyChangeJobStatus] || this.transportCompanyChangeJobStatus || 'Sin estado';
  }

  get transportCompanyChangeJobStatusDescription(): string {
    const descriptions: { [status: string]: string } = {
      PENDING: 'El proceso está en cola y comenzará en segundo plano.',
      VALIDATING: 'Se están validando la carga y la información del cambio.',
      CANCELLING: 'Se están anulando los documentos RNDC asociados a la información anterior.',
      SWITCHING: 'Se está actualizando la empresa de transporte y/o el contratante.',
      RECREATING: 'Se están generando nuevamente los documentos RNDC con la nueva información.',
      DONE: 'La información del servicio fue actualizada correctamente.',
      FAILED: 'El proceso se detuvo. Consulta el detalle del error para conocer la causa.'
    };
    return descriptions[this.transportCompanyChangeJobStatus] || 'El backend no informó un estado reconocible.';
  }

  get transportCompanyChangeJobProgress(): number {
    const progress: { [status: string]: number } = {
      PENDING: 8,
      VALIDATING: 20,
      CANCELLING: 40,
      SWITCHING: 65,
      RECREATING: 85,
      DONE: 100,
      FAILED: 100
    };
    return progress[this.transportCompanyChangeJobStatus] || 0;
  }

  get transportCompanyChangeJobStatusClass(): string {
    return this.transportCompanyChangeJobStatus
      ? this.transportCompanyChangeJobStatus.toLowerCase()
      : 'unknown';
  }

  get transportCompanyChangeCurrentStepLabel(): string {
    const currentStep = this.transportCompanyChangeJob && this.transportCompanyChangeJob.currentStep;
    if (!currentStep) return '';
    return getTransportCompanyChangeStepLabel(
      currentStep,
      this.transportCompanyChangeJobStatus
    );
  }

  get cancelledTransportDocuments(): string[] {
    const cancelled = this.transportCompanyChangeJob && this.transportCompanyChangeJob.cancelled;
    return cancelled ? cancelled.map(document => this.getTransportDocumentLabel(document)) : [];
  }

  get recreatedTransportDocuments(): string[] {
    const recreated = this.transportCompanyChangeJob && this.transportCompanyChangeJob.recreated;
    return recreated ? recreated.map(document => this.getTransportDocumentLabel(document)) : [];
  }

  private async loadActiveTransportCompanyChangeJob(): Promise<void> {
    const cargo = this.paramsDialog && this.paramsDialog.cargo;
    if (!shouldQueryTransportCompanyChangeJob(cargo, this.isContractorTransportDialog)) return;
    const jobId = cargo.activeTransportCompanyChangeJobId;

    this.activeTransportCompanyChangeJobId = jobId;
    this.isLoadingTransportCompanyChangeJob = true;
    this.transportCompanyChangeJobError = '';
    try {
      const job = await this.menuService.getTransportCompanyChangeJob(jobId).toPromise();
      this.transportCompanyChangeJob = {
        ...job,
        id: job && job.id ? job.id : jobId
      };
    } catch (error) {
      this.transportCompanyChangeJobError = this.getTransportCompanyChangeErrorMessage(error, true);
    } finally {
      this.isLoadingTransportCompanyChangeJob = false;
    }
  }

  async refreshTransportCompanyChangeJob(): Promise<void> {
    if (this.isLoadingTransportCompanyChangeJob || this.isRefreshingTransportCompanyChangeJob) return;
    const jobId = this.activeTransportCompanyChangeJobId
      || (this.transportCompanyChangeJob && this.transportCompanyChangeJob.id);
    if (!jobId) {
      this.snackBarService.openSnackBar(
        'No se encontró el identificador del proceso. Cierra y vuelve a abrir el diálogo.',
        undefined,
        'alert'
      );
      return;
    }

    this.isRefreshingTransportCompanyChangeJob = true;
    try {
      const job = await this.menuService.getTransportCompanyChangeJob(jobId).toPromise();
      this.transportCompanyChangeJob = {
        ...job,
        id: job && job.id ? job.id : jobId
      };
      this.transportCompanyChangeJobError = '';
      this.snackBarService.openSnackBar('Estado del proceso actualizado correctamente');
    } catch (error) {
      const errorMessage = this.getTransportCompanyChangeErrorMessage(error, true);
      if (!this.transportCompanyChangeJob)
        this.transportCompanyChangeJobError = errorMessage;
      this.snackBarService.openSnackBar(errorMessage, undefined, 'error');
    } finally {
      this.isRefreshingTransportCompanyChangeJob = false;
    }
  }

  private getTransportDocumentLabel(document: string): string {
    const labels: { [document: string]: string } = {
      remesa: 'Remesa',
      manifiesto: 'Manifiesto',
      cumplido: 'Cumplido'
    };
    return labels[document] || document;
  }

  private setLocalValues() {
    const idCompany = this.menuService.form.get('idCompany').value;
    this.selectedCompanyId = idCompany || '';
    const transportCompany = this.menuService.form.get('transportCompany').value;
    this.originalTransportCompany = transportCompany ? this.utils.clone(transportCompany) : null;
    this.transportCompanyControl.setValue(transportCompany || null, { emitEvent: false });
    this.companyOptions = { ...this.companyOptions, initialNit: idCompany ? idCompany : '' };
    const tripType = this.utils.getNestedValue(this.menuService.form.value, 'cargoModel.tripType.name');
    this.tripTypeControl.setValue(tripType ? tripType : '');
    const ministry = this.utils.getNestedValue(this.menuService.form.value, 'ministry');
    this.ministryControl.setValue(this.utils.isDefined(ministry) ? ministry : null);
    const labels = this.utils.getNestedValue(this.menuService.form.value, 'labels');
    this.tagsSaved = this.utils.isDefined(labels) ? labels : [];
    const numberDocumentSender = this.utils.getNestedValue(this.menuService.form.value, 'numberDocumentSender');
    this.numberDocumentSenderControl.setValue(this.utils.isDefined(numberDocumentSender) ? numberDocumentSender : '');
    this.tripTypes.forEach(tripType => {
      const isValid = this.isEnabledTripType(tripType);
      tripType['isValid'] = isValid.isValid;
      tripType['error'] = isValid.error;
    });
  }

  private setSubscription() {
    this.formSubscription = this.menuService.formObservable.subscribe((cargo) => {
      this.setLocalValues();
    });
    this.transportCompanySubscription = this.companyControl.valueChanges.subscribe((company: Company) => {
      const companyId = company && company.companyId ? company.companyId : '';
      if (companyId !== this.selectedCompanyId) {
        this.selectedCompanyId = companyId;
        this.setDefaultTransportCompany(company);
      }
    });
  }

  private setDefaultTransportCompany(company: Company): void {
    if (!company || !company.transportCompany) return;
    if (!this.areTransportCompaniesEqual(this.transportCompanyControl.value, company.transportCompany))
      this.transportCompanyControl.setValue(company.transportCompany);
  }

  private checkPermissionsToEdit(): void {
    if (!this.canEditGeneralData) {
      this.tripTypeControl.disable();
      this.ministryControl.disable();
      this.cargoOwnerAddressControl.disable();
      this.cargoOwnerAddressValidate = 'disable';
      this.companyControl.disable();
      this.companyValidate = 'disable';
      this.numberDocumentSenderControl.disable();
    }
    if (!this.canEditTags)
      this.tagControl.disable();
    if (this.isDialog) {
      this.ministryControl.disable();
      const cargoState = this.utils.getNestedValue(this.menuService.cargo, 'state');
      if (cargoState === CargoStateEnum.END_SERVICE) {
        this.tripTypeControl.disable();
        this.tagControl.disable();
      }
      const approvalState = this.utils.getNestedValue(this.menuService.cargo, 'approval');
      if (approvalState === ApprovalStateEnum.APPROVED)
        this.tagControl.disable();
    }
  }

  get cargoStateEnum(): typeof CargoStateEnum {
    return CargoStateEnum;
  }

  private getTags(): void {
    this.menuService.getLabelTags().subscribe(
      (success) => this.tags = success && success.length ? success : [],
      () => this.tags = []
    );
  }

  selectTag(event: MatSelectChange): void {
    const selectedTag: string = event.value;
    this.tagsSaved.push(selectedTag);
    this.tagControl.setValue(null);
  }

  clearTag(index: number): void {
    this.tagsSaved.splice(index, 1);
  }

  private isEnabledTripType(tripType: { label: string, name: TRIP_TYPES }): { isValid: boolean, error: string } {
    if (!this.isDialog) return { isValid: true, error: '' };
    const isUrbanService = this.utils.isSameCity(this.menuService.cargo);
    if (!isUrbanService && [TRIP_TYPES.URBAN, TRIP_TYPES.LAST_MILE].includes(tripType.name)) return { isValid: false, error: 'No es posible seleccionar este tipo de viaje en un servicio que tiene origen y destino en distintas ciudades' };
    if (isUrbanService && [TRIP_TYPES.NATIONAL, TRIP_TYPES.INTERNATIONAL].includes(tripType.name)) return { isValid: false, error: 'No es posible seleccionar este tipo de viaje en un servicio que tiene origen y destino en la misma ciudad' };
    if (this.menuService.hasOwnFleetException() || this.menuService.isEscortedService() || this.menuService.isSatelliteLockService()) return { isValid: true, error: '' };
    if (this.menuService.hasPermissionNoMinimumUtility()) return { isValid: true, error: '' };
    const rate = this.utils.getNestedValue(this.menuService.cargo, 'shippingCost.rate');
    const freightCost = this.utils.getNestedValue(this.menuService.cargo, 'shippingCost.freightCost');
    const idCompany = this.utils.getNestedValue(this.menuService.cargo, 'idCompany');
    const minUtility = this.companyManager.getUtilityByCompanyId(idCompany);
    const isValid = rate && freightCost && minUtility && this.menuService.utilityCargo < minUtility;
    if (isValid) return { isValid: true, error: '' };
    if (this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.createCargoWithRateOrCostZero)) return { isValid: true, error: '' };
    if (this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.createCargosWithoutMinimumUtility) && tripType.name === TRIP_TYPES.URBAN) return { isValid: true, error: '' };
    return { isValid: false, error: "El cambio de tipo de viaje afecta la utilidad mínima, no es posible realizar el cambio" };
  }

  get showMinistrySelector(): boolean {
    const hasPermission = this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.changeManifestRequirement);
    const isTransportService = this.menuService.isTransportService();
    return hasPermission && isTransportService;
  }

  get canSeeGeneralData(): boolean {
    return this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.allowToEditGeneralDataModule)
  }

  get canEditGeneralData(): boolean {
    return this.canSeeGeneralData && this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editCargo)
  }

  get isServiceRequest(): boolean {
    return !!(this.menuService.cargo && this.menuService.cargo.state === CargoStateEnum.REQUEST);
  }

  get hasTransportCompanyChanged(): boolean {
    return !this.areTransportCompaniesEqual(this.originalTransportCompany, this.transportCompanyControl.value);
  }

  get hasContractorChanged(): boolean {
    const originalCompanyId = this.menuService.cargo && this.menuService.cargo.idCompany
      ? this.menuService.cargo.idCompany
      : '';
    const selectedCompany = this.companyControl.value;
    if (!selectedCompany || !selectedCompany.companyId) return false;
    const selectedCompanyId = selectedCompany.companyId;
    if (originalCompanyId !== selectedCompanyId) return true;

    if (!this.ministryControl.value) return false;
    const originalBranchOffice = this.utils.getNestedValue(this.menuService.cargo, 'cargoOwner.branchOffice') || '';
    const selectedBranchOffice = this.utils.getNestedValue(this.cargoOwnerAddressControl.value, 'address.id') || '';
    if (!selectedBranchOffice) return false;
    return `${originalBranchOffice}` !== `${selectedBranchOffice}`;
  }

  /**
   * El cambio solo anula y recrea documentos RNDC cuando el servicio los tiene.
   * No los tiene una SaaS sin servicios de transporte, que nunca los emite; una
   * solicitud, que todavía no los generó; ni un servicio sin requerimiento de
   * manifiesto. En esos casos el cambio es un guardado corriente y no
   * corresponde advertir ni pedir confirmación.
   */
  get willRecreateRndcDocuments(): boolean {
    return this.requiresTransportCompany
      && !this.isServiceRequest
      && !!this.ministryControl.value;
  }

  get hasContractorOrTransportCompanyChanged(): boolean {
    return this.hasContractorChanged || this.hasTransportCompanyChanged;
  }

  get canSeeTags(): boolean {
    return this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.showEditTagsModule);
  }

  get canEditTags(): boolean {
    return this.canSeeTags && this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editCargoTags);
  }

  private isValidForm(): boolean {
    if (this.showAdditionalInformationSection && this.utils.errorMessagesCustomized(this.tripTypeControl, 'tipo de viaje'))
      return false;
    if (this.showContractorTransportSection && this.utils.errorMessagesCustomized(this.companyControl, 'contratante del servicio'))
      return false;
    const hasMinistry = !!this.ministryControl.value;
    if (this.showContractorTransportSection && hasMinistry && this.utils.errorMessagesCustomized(this.cargoOwnerAddressControl, 'dirección del contratante'))
      return false;
    if (this.showContractorTransportSection && this.utils.errorMessagesCustomized(this.transportCompanyControl, 'empresa de transporte'))
      return false;
    return true;
  }

  async saveAndContinue() {
    if (this.isContractorTransportDialog) {
      this.saveContractorAndTransportCompany();
      return;
    }
    if (!this.isDialog && this.utils.errorMessagesCustomized(this.transportCompanyControl, 'empresa de transporte')) return;
    if (this.isDialog && !this.isValidForm()) return;
    const prevMinistry = this.menuService.form.get('ministry').value;
    const newMinistry = !this.menuService.isTransportService() ? false : this.utils.isDefined(this.ministryControl.value) ? this.ministryControl.value : null;
    this.menuService.form.get('ministry').setValue(newMinistry);
    if (!this.isDialog && this.utils.isDefined(prevMinistry) && prevMinistry !== newMinistry) {
      this.resetOriginAndDestinationBlocks();
      this.menuService.form.get('containers').setValue([]);
    }
    this.menuService.form.get('numberDocumentSender').setValue(this.numberDocumentSenderControl.value);
    this.menuService.form.get('cargoModel.tripType.name').setValue(this.tripTypeControl.value);
    // Se espera: si cambió la empresa, la reteica recalculada debe ir en el body que se guarda.
    if (this.isServiceRequest) await this.applyContractorAndTransportCompanyToForm();
    this.menuService.form.get('labels').clear();
    this.tagsSaved.forEach(tag => this.menuService.form.get('labels').value.push(tag));

    const oldConsignments: ConsignmentCargo[] = this.utils.clone(this.menuService.cargoConsignments);
    const oldCargo: Cargo = this.utils.clone(this.menuService.cargo);
    const guideChanges = this.checkGuideChange(oldCargo, this.menuService.form.value);
    const cargoId = this.menuService.cargo.id;
    const body = this.menuService.getBodyUpdateOnlyRequest();
    this.saveService(body, cargoId, oldConsignments, oldCargo, guideChanges);
  }

  private saveService(
    body: Cargo,
    cargoId: string,
    oldConsignments: ConsignmentCargo[],
    oldCargo: Cargo,
    guideChanges: { [key: string]: any },
    overrideRiskValidation?: boolean
  ) {
    this.menuService.updateServiceWithBody(body, overrideRiskValidation).then(async () => {
      const guidesIds = this.utils.cargoCreatedGuide(this.menuService.form.value);
      if (guidesIds.length && this.isDialog && Object.keys(guideChanges).length) await this.updateGuides(guidesIds, guideChanges, cargoId);
      if (oldConsignments.length && oldCargo.state === CargoStateEnum.REQUEST)
        await this.checkConsignmentsToChange(oldConsignments, oldCargo);
      this.snackBarService.openSnackBar("Servicio actualizado exitosamente");
      if (!this.isDialog) this.continueToNextStep();
      else this.dialogRef.close({ state: true });
    }).catch(async (error) => {
      if (error && error.error && error.error.code === VEHICLE_TRIP_VALIDATION_EXCEPTION_CODE) {
        const { continueAnyway } = await this.cargoManager.showRiskValidationBlockedDialog(error.error.message);
        if (continueAnyway) return this.saveService(body, cargoId, oldConsignments, oldCargo, guideChanges, true);
      }
      this.snackBarService.openSnackBar('Ocurrió un error al actualizar el servicio', undefined, 'error');
    });
  }

  async saveContractorAndTransportCompany(): Promise<void> {
    if (this.showTransportCompanyChangeJob || this.isSubmittingTransportCompanyChange) return;
    if (this.utils.errorMessagesCustomized(this.companyControl, 'contratante del servicio')) return;
    if (this.ministryControl.value && this.utils.errorMessagesCustomized(this.cargoOwnerAddressControl, 'dirección del contratante')) return;
    if (this.utils.errorMessagesCustomized(this.transportCompanyControl, 'empresa de transporte')) return;

    const body = this.getContractorAndTransportCompanyRequest();
    if (!body.idCompany && !body.transportCompany) {
      this.snackBarService.openSnackBar('No hay cambios por guardar', undefined, 'info');
      return;
    }

    // Sin recreación de documentos no hay nada que confirmar: se guarda directo.
    if (this.willRecreateRndcDocuments && this.hasContractorOrTransportCompanyChanged) {
      const confirmed = await this.confirmContractorAndTransportCompanyChange();
      if (!confirmed) return;
    }

    await this.submitContractorAndTransportCompanyChange(body);
  }

  private async submitContractorAndTransportCompanyChange(body: ChangeTransportCompanyRequest): Promise<void> {
    this.isSubmittingTransportCompanyChange = true;
    try {
      const cargoId = this.menuService.cargo && this.menuService.cargo.id;
      const response = await this.menuService.updateContractorAndTransportCompany(cargoId, body).toPromise();
      if (this.willRecreateRndcDocuments) {
        this.setTransportCompanyChangeJobFromResponse(response);
        this.snackBarService.openSnackBar('El cambio fue programado correctamente y continuará en segundo plano');
      } else {
        // Sin recreación no hay proceso que seguir: rastrear un jobId dejaría el
        // panel de progreso y bloquearía la edición del servicio sin motivo.
        await this.menuService.refreshCargo();
        this.snackBarService.openSnackBar('Los cambios fueron guardados exitosamente');
        this.dialogRef.close({ state: true });
      }
    } catch (error) {
      if (error && error.error && error.error.code === VEHICLE_TRIP_VALIDATION_EXCEPTION_CODE) {
        this.isSubmittingTransportCompanyChange = false;
        const { continueAnyway } = await this.cargoManager.showRiskValidationBlockedDialog(error.error.message);
        if (continueAnyway) await this.submitContractorAndTransportCompanyChange({ ...body, overrideRiskValidation: true });
        return;
      }
      this.snackBarService.openSnackBar(
        this.getTransportCompanyChangeErrorMessage(error),
        undefined,
        'error'
      );
    } finally {
      this.isSubmittingTransportCompanyChange = false;
    }
  }

  onCargoOwnerAddressSelected(): void {
    if (!this.isDialog) this.saveAndContinue();
  }

  private async applyContractorAndTransportCompanyToForm(): Promise<void> {
    const company: Company = this.companyControl.value;
    if (company.companyId !== this.menuService.cargo.idCompany) {
      this.menuService.form.get('requiresPreBill').setValue(!!this.utils.getNestedValue(company, 'billingConfiguration.requiresPreInvoice'));
      if (company.billingConfiguration && company.billingConfiguration.expirationDays)
        this.menuService.form.get('shippingCost.expirationBillDays').setValue(company.billingConfiguration.expirationDays);
    }
    const previousTransportCompanyId = this.transportCompanyIdOf(this.menuService.form.get('transportCompany').value);
    this.menuService.form.get('transportCompany').setValue(this.transportCompanyControl.value);
    const transportCompanyId = this.transportCompanyIdOf(this.transportCompanyControl.value);
    if (transportCompanyId !== previousTransportCompanyId)
      await this.syncWithTransportCompany(previousTransportCompanyId, transportCompanyId);
    if (!this.menuService.form.get('ministry').value) {
      this.menuService.form.get('cargoOwner').patchValue(this.cargoResources.cargoMock.cargoOwner);
      this.menuService.form.get('idCompany').setValue(company.companyId ? company.companyId : '');
      return;
    }
    this.menuService.form.get('cargoOwner.documentNumber').setValue(company.companyId ? company.companyId : '');
    this.menuService.form.get('cargoOwner.documentType').setValue(company.companyId ? '3' : '');
    this.menuService.form.get('cargoOwner.name').setValue(company.name ? company.name : '');
    this.menuService.form.get('idCompany').setValue(company.companyId ? company.companyId : '');
    const cargoOwnerAddress = this.cargoOwnerAddressControl.value;
    this.menuService.form.get('cargoOwner.address').setValue(cargoOwnerAddress && cargoOwnerAddress.branchOfficeName ? cargoOwnerAddress.branchOfficeName : cargoOwnerAddress.address ? cargoOwnerAddress.address.address : '');
    this.menuService.form.get('cargoOwner.branchOffice').setValue(cargoOwnerAddress && cargoOwnerAddress.address ? cargoOwnerAddress.address.id : '');
  }

  private transportCompanyIdOf(transportCompany: TransportCompany): string {
    return transportCompany && transportCompany.id ? transportCompany.id : null;
  }

  /**
   * Los servicios adicionales y la reteica del origen dependen de la empresa de
   * transporte. Si se cambia una empresa ya elegida se quitan los servicios
   * adicionales agregados; la reteica del origen se recalcula con la nueva
   * empresa (sin obligar a elegir la ciudad otra vez) y si no tiene, queda en 0
   * y el paso "Origen" pide corregirla como hoy.
   */
  private async syncWithTransportCompany(previousTransportCompanyId: string, transportCompanyId: string): Promise<void> {
    const form = this.menuService.form;
    const additionalCosts = form.get('additionalCosts');
    if (previousTransportCompanyId && additionalCosts && additionalCosts.value && additionalCosts.value.length) {
      if (additionalCosts instanceof FormArray) additionalCosts.clear();
      else additionalCosts.setValue([]);
      this.snackBarService.openSnackBar(ServiceMessages.TRANSPORT_COMPANY_CHANGED_ADDITIONAL_COSTS_CLEARED, undefined, 'alert');
    }
    const origin = this.utils.getNestedValue(form.value, 'cargoFeature.uploadDownload.origin') || {};
    if (!origin.municipalityCode || origin.isInternational) return;
    // Si la ciudad no aparece para la nueva empresa (o la consulta falla) queda en 0:
    // no se puede conservar la reteica de la empresa anterior.
    const reteica = (await this.originReteicaService.resolve(origin.municipalityCode, transportCompanyId)) || 0;
    form.get('cargoFeature.uploadDownload.origin.reteica').setValue(reteica);
    form.get('shippingCost.reteicaPercentage').setValue(reteica && !this.menuService.hasTaxExceptions() ? reteica : 0);
    if (!reteica && this.menuService.isRequiredReteica())
      this.snackBarService.openSnackBar(ServiceMessages.ORIGIN_CITY_WITHOUT_RETEICA_FOR_TRANSPORT_COMPANY, undefined, 'alert');
  }

  private getContractorAndTransportCompanyRequest(): ChangeTransportCompanyRequest {
    const body: ChangeTransportCompanyRequest = {};
    if (this.hasTransportCompanyChanged) {
      const transportCompany: TransportCompany = this.transportCompanyControl.value;
      body.transportCompany = {
        nit: transportCompany && transportCompany.id ? transportCompany.id : '',
        name: transportCompany && transportCompany.name ? transportCompany.name : ''
      };
    }
    if (this.hasContractorChanged) {
      const company: Company = this.companyControl.value;
      const companyId = company && company.companyId ? company.companyId : '';
      body.idCompany = companyId;
      body.cargoOwner = this.getCargoOwnerPayload();
      body.expirationBillDays = company.billingConfiguration && company.billingConfiguration.expirationDays ? company.billingConfiguration.expirationDays : 1;
      body.requiresPreBill = !!this.utils.getNestedValue(company, 'billingConfiguration.requiresPreInvoice');
    }
    if (body.transportCompany && body.idCompany)
      body.reason = 'Cambio de empresa de transporte y contratante desde el detalle del servicio';
    else if (body.transportCompany)
      body.reason = 'Cambio de empresa de transporte desde el detalle del servicio';
    else if (body.idCompany)
      body.reason = 'Cambio de contratante desde el detalle del servicio';
    return body;
  }

  private getCargoOwnerPayload(): ThirdParty {
    const company = this.companyControl.value;
    const cargoOwnerAddress = this.cargoOwnerAddressControl.value;
    const currentCargoOwner: ThirdParty = this.menuService.cargo && this.menuService.cargo.cargoOwner
      ? this.utils.clone(this.menuService.cargo.cargoOwner)
      : {};
    const companyId = company && company.companyId ? company.companyId : '';
    const branchOffice = cargoOwnerAddress && cargoOwnerAddress.address && this.utils.isDefined(cargoOwnerAddress.address.id)
      ? `${cargoOwnerAddress.address.id}`
      : currentCargoOwner.branchOffice || '';
    const address = cargoOwnerAddress && cargoOwnerAddress.branchOfficeName
      ? cargoOwnerAddress.branchOfficeName
      : cargoOwnerAddress && cargoOwnerAddress.address && cargoOwnerAddress.address.address
        ? cargoOwnerAddress.address.address
        : currentCargoOwner.address || '';
    const addressId = this.getNumericValue(
      cargoOwnerAddress && cargoOwnerAddress.addressId,
      currentCargoOwner.addressId
    );
    const cityId = this.getNumericValue(
      cargoOwnerAddress && cargoOwnerAddress.cityId,
      currentCargoOwner.cityId
    );
    return {
      ...currentCargoOwner,
      documentType: currentCargoOwner.documentType || '3',
      documentNumber: companyId,
      name: company && company.name ? company.name : '',
      branchOffice,
      fromDestination: this.utils.isDefined(currentCargoOwner.fromDestination)
        ? currentCargoOwner.fromDestination
        : false,
      addressId,
      cityId,
      address,
      municipalityCode: cargoOwnerAddress && cargoOwnerAddress.municipalityCode
        ? cargoOwnerAddress.municipalityCode
        : currentCargoOwner.municipalityCode,
      city: cargoOwnerAddress && cargoOwnerAddress.information && cargoOwnerAddress.information.city
        ? cargoOwnerAddress.information.city
        : currentCargoOwner.city,
      location: cargoOwnerAddress && cargoOwnerAddress.address && cargoOwnerAddress.address.location
        ? cargoOwnerAddress.address.location
        : currentCargoOwner.location
    };
  }

  private getNumericValue(value: any, fallback: number): number {
    if (this.utils.isDefined(value) && value !== '' && !isNaN(Number(value))) return Number(value);
    if (this.utils.isDefined(fallback) && !isNaN(Number(fallback))) return Number(fallback);
    return 0;
  }

  private async confirmContractorAndTransportCompanyChange(): Promise<boolean> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Confirmar cambio de contratante o empresa de transporte',
      iconAlert: true,
      description: 'Este cambio iniciará un proceso en segundo plano. Las remesas, el manifiesto y el cumplido '
        + 'asociados se anularán y se recrearán automáticamente con la nueva información.',
      hideBtnCancel: false,
      hideBtnConfirm: false
    };
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.width = ModalEnum.SMALL_WIDTH;
    dialogConfig.autoFocus = false;
    const result = await this.dialog.open(DialogComponent, dialogConfig).afterClosed().toPromise();
    return !!(result && result.state);
  }

  private setTransportCompanyChangeJobFromResponse(response: ChangeTransportCompanyResponse): void {
    if (!response || !response.jobId)
      throw new Error('El backend no devolvió el identificador del proceso');
    this.activeTransportCompanyChangeJobId = response.jobId;
    if (this.menuService.cargo)
      this.menuService.cargo.activeTransportCompanyChangeJobId = response.jobId;
    this.transportCompanyChangeJobError = '';
    this.transportCompanyChangeJob = {
      id: response.jobId,
      cargoId: response.cargoId,
      status: response.status || 'PENDING',
      cancelled: [],
      recreated: []
    };
  }

  private getTransportCompanyChangeErrorMessage(error: any, isStatusQuery: boolean = false): string {
    const backendMessage = error && typeof error.error === 'string'
      ? error.error
      : error && error.error && error.error.message
        ? error.error.message
        : '';
    if (backendMessage) return backendMessage;
    if (isStatusQuery && error && error.status === 404)
      return 'No se encontró el proceso asociado a este servicio. Recarga el detalle antes de intentarlo nuevamente.';
    if (error && error.status === 409)
      return 'No es posible iniciar el cambio porque la carga está facturada, pagada o ya tiene un proceso activo.';
    if (error && error.status === 400)
      return 'La información del cambio está incompleta o no es válida.';
    if (error && error.status === 404)
      return 'No se encontró la carga asociada al cambio.';
    return isStatusQuery
      ? 'No fue posible consultar el estado del proceso. Cierra y vuelve a abrir el diálogo para intentarlo nuevamente.'
      : 'Ocurrió un error al iniciar el cambio de contratante o empresa de transporte.';
  }

  closeDialog(): void {
    this.dialogRef.close({
      state: !!this.transportCompanyChangeJob
    });
  }

  private areTransportCompaniesEqual(first: TransportCompany, second: TransportCompany): boolean {
    return !!(first && second) ? first.id === second.id : first === second;
  }

  private resetOriginAndDestinationBlocks(): void {
    const originGroup = this.menuService.form.get('cargoFeature.uploadDownload.origin');
    if (originGroup) {
      originGroup.get('name').setValue('');
      originGroup.get('municipalityCode').setValue('');
      originGroup.get('country.name').setValue('Colombia');
      originGroup.get('country.code').setValue('CO');
      originGroup.get('reteica').setValue(0);
      const isInternationalCtrl = originGroup.get('isInternational');
      if (isInternationalCtrl) {
        isInternationalCtrl.setValue(false);
      }
      const originAddresses = originGroup.get('addresses') as FormArray;
      if (originAddresses) {
        originAddresses.clear();
      }
    }
    const destinationArray = this.menuService.form.get('cargoFeature.uploadDownload.destination') as FormArray;
    if (destinationArray) {
      destinationArray.clear();
    }
    const reteicaCtrl = this.menuService.form.get('shippingCost.reteicaPercentage');
    if (reteicaCtrl) {
      reteicaCtrl.setValue(0);
    }
  }

  private async checkConsignmentsToChange(oldConsignments: ConsignmentCargo[], oldCargo: Cargo) {
    const hasToDeleteConsignmentByMinistry = oldCargo && oldCargo.ministry && !this.menuService.form.get('ministry').value;

    const newCargoOwner = this.utils.getNestedValue(this.menuService.form.value, 'cargoOwner');
    const oldCargoOwner = this.utils.getNestedValue(oldCargo, 'cargoOwner');
    const hasToUpdateConsignmentByOwner = oldCargoOwner && newCargoOwner && (oldCargoOwner.documentNumber !== newCargoOwner.documentNumber
      || oldCargoOwner.branchOffice !== newCargoOwner.branchOffice);
    this.spinner.show();
    if (hasToDeleteConsignmentByMinistry)
      await this.menuService.deleteAllCargoConsignments();
    else if (hasToUpdateConsignmentByOwner) {
      const consignmentsToUpdate = oldConsignments.map(consignment => {
        consignment.cargoOwner = newCargoOwner;
        return consignment;
      });
      await this.menuService.updateAllCargoConsignments(consignmentsToUpdate);
    }
    await this.menuService.setCargoConsignments();
    this.spinner.hide();
  }

  continueToNextStep() {
    this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.origin, this.menuService.cargo.consecutive)]);
  }

  private checkGuideChange(previousCargo: Cargo, newCargo: Cargo) {
    let guideChanges = {};
    const previousValue = this.utils.getNestedValue(previousCargo, 'idCompany');
    const newValue = this.utils.getNestedValue(newCargo, 'idCompany');
    const companyName = this.companyControl.value && this.companyControl.value.name ? this.companyControl.value.name : '';
    if (previousValue && newValue && previousValue !== newValue) {
      guideChanges['companyId'] = newValue;
      guideChanges['sender'] = {};
      guideChanges['sender']['nameCompany'] = this.utils.clone(companyName);
    }

    return guideChanges;
  }

  private async updateGuides(guidesIds: string[], guideChanges: any, cargoId: string) {
    const guides = guidesIds.map(guideId => {
      return {
        guideId: guideId,
        ...guideChanges
      }
    });
    try {
      await this.guideService.updateGuides(guides, cargoId).toPromise();
    } catch (error) {
      console.error(error);
      return this.snackBarService.openSnackBar('Ocurrió un error al actualizar las guías', undefined, 'error');
    }
    return true;
  }

  ngOnDestroy() {
    if (this.formSubscription) this.formSubscription.unsubscribe();
    if (this.transportCompanySubscription) this.transportCompanySubscription.unsubscribe();
  }
}
